import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, Row, StyleProps } from "@hex-insights/core";
import { Form, FormEditButtonPair, FormState, SubmitButton, useInternalField } from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import {
	SocialComment,
	SocialCommentCreateMutation,
	SocialCommentDetailQuery,
	SocialCommentUpdateMutation,
} from "../../../Utilities";
import { SocialCommentFormState, SocialCommentFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";
import styles from "./styles.module.css";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<SocialCommentCreateMutation["createSocialComment"]>,
	"formState" | "create"
> & {
	formStateArgs?: SocialCommentFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the SocialComment model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<SocialCommentFormValues.Create>;
	create: CreateFunction<SocialCommentFormValues.Create, T>;
	onSuccess: (formState: FormState<SocialCommentFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<SocialCommentFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the SocialComment model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "socialComment.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<SocialCommentFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.Body formState={formState} />
			<Field.MediaAttachment.Select formState={formState} />
			<Field.Newsletter.Select formState={formState} />
			<Field.Post.Select formState={formState} />
		</React.Fragment>
	);
}

export type ControlledCreateOnExistingProps = Omit<
	CreateOnExistingProps<SocialCommentCreateMutation["createSocialComment"]>,
	"formState" | "create"
> & {
	formStateArgs?: SocialCommentFormState.UseCreateFormStateArgs;
};

export function ControlledCreateOnExisting({ formStateArgs, ...props }: ControlledCreateOnExistingProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <CreateOnExisting formState={formState} create={create} {...props} />;
}

export type CreateOnExistingProps<T> = CreateProps<T> & {
	disabled?: boolean;
};

export function CreateOnExisting<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	disabled = false,
	...props
}: CreateOnExistingProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "socialComment.create" + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalField(formState, "mediaAttachmentID");
	useInternalField(formState, "newsletterID");
	useInternalField(formState, "postID");
	useInternalField(formState, "studentNoteID");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Row justify="spaced-start" align="flex-end" horizontalSpacing="0.25rem">
				<Field.Body
					formState={formState}
					noHeader
					placeholder="Add a comment..."
					disabled={disabled}
					style={{ flexGrow: 1 }}
				/>

				<SubmitButton
					variant="link"
					size="small"
					submissionStatus={formState.submissionStatus}
					onClick={formState.onSubmitWrapper(onSubmit)}
					disabled={formState.formValues.body === "" || disabled}
					className={styles["button--icon-circle"]}
				>
					<Icon.PaperPlaneTilt size={14} className={styles["button--icon-circle__icon"]} />
				</SubmitButton>
			</Row>
		</Form>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<SocialCommentUpdateMutation["updateSocialComment"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<SocialCommentFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the SocialComment model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, socialComment: props.socialComment });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	socialComment: SocialCommentDetailQuery["socialComment"];
	formState: FormState<SocialCommentFormValues.Detail>;
	update: UpdateFunction<SocialCommentFormValues.Detail, T>;
	onSuccess: (formState: FormState<SocialCommentFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<SocialCommentFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the SocialComment model using the given form state.
 */
export function Detail<T>({ formState, socialComment, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `socialComment.detail.${socialComment.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} socialComment={socialComment} />
			</Form.Column>
		</Form>
	);
}

export type ControlledDetailOnExistingProps = Omit<
	DetailOnExistingProps<SocialCommentUpdateMutation["updateSocialComment"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<SocialCommentFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

export function ControlledDetailOnExisting({ formStateArgs, ...props }: ControlledDetailOnExistingProps) {
	const { formState, update } = useDetailFormControl<"body">({
		...formStateArgs,
		socialComment: props.socialComment,
		fields: { body: true },
	});

	return <DetailOnExisting formState={formState} update={update} {...props} />;
}

export type DetailOnExistingProps<T> = Omit<DetailProps<T>, "socialComment" | "formState" | "update" | "onSuccess"> & {
	socialComment: Pick<SocialComment, "id" | "body">;
	formState: FormState<SocialCommentFormValues.DetailSubset<"body">>;
	update: UpdateFunction<SocialCommentFormValues.DetailSubset<"body">, T>;
	onSuccess: (formState: FormState<SocialCommentFormValues.DetailSubset<"body">>, data: T) => void;
};

export function DetailOnExisting<T>({
	formState,
	socialComment,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailOnExistingProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `socialComment.detail.${socialComment.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<Field.Body formState={formState} noHeader style={{ flexGrow: 1 }} />

				<Form.ActionBar>
					<SubmitButton
						submissionStatus={formState.submissionStatus}
						onClick={formState.onSubmitWrapper(onSubmit)}
						disabled={formState.formValues.body === ""}
					>
						Save
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<SocialCommentFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the SocialComment model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, socialComment: props.socialComment });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	socialComment: SocialCommentDetailQuery["socialComment"];
	formState: FormState<SocialCommentFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the SocialComment model using the given form state.
 */
export function ReadOnly({ formState, socialComment, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} socialComment={socialComment} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	socialComment: SocialCommentDetailQuery["socialComment"];
	formState: FormState<SocialCommentFormValues.Detail>;
};

function DetailFields({ formState, socialComment }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.CreatedAt formState={formState} />
			<Field.Body formState={formState} />
			<Field.Author.Select formState={formState} currentInstance={socialComment.author} />
			<Field.MediaAttachment.Select formState={formState} currentInstance={socialComment.mediaAttachment} />
			<Field.Newsletter.Select formState={formState} currentInstance={socialComment.newsletter} />
			<Field.Post.Select formState={formState} currentInstance={socialComment.post} />
		</React.Fragment>
	);
}
